import Card from "@components/Card";
import React, { FC, useEffect, useRef } from "react";
import CloseSmall from "@icon-park/react/lib/icons/CloseSmall";
import { CloseButton, Overlay, Window } from "./styled";
import { useTheme } from "styled-components";

export type ModalWindowProps = {
  show: boolean;
  width?: string;
  height?: string;
  minHeight?: string;
  maxHeight?: string;
  padding?: number;
  ignoreHeightAndScroll?: boolean;
  isCloseable?: boolean;
  showCloseButton?: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  /** Id of the element that names the dialog. */
  labelledBy?: string;
  /** Moves focus into the dialog, keeps Tab inside it and hands focus back on close. */
  manageFocus?: boolean;
};

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export const ModalWindow: FC<ModalWindowProps> = ({
  show = false,
  width,
  height,
  minHeight,
  maxHeight,
  padding,
  onClose,
  ignoreHeightAndScroll,
  children,
  isCloseable = true,
  showCloseButton,
  labelledBy,
  manageFocus = false,
}) => {
  const theme = useTheme();
  const windowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      const shouldClose = event.key === "Escape" && isCloseable && onClose;
      if (shouldClose) {
        onClose();
      }
    };

    if (show) {
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [show]);

  useEffect(() => {
    const dialog = windowRef.current;
    if (!show || !manageFocus || !dialog) return;

    const returnTo =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    dialog.focus();

    const keepTabInside = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE)
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const isOnEdge = event.shiftKey ? active === first : active === last;
      if (!first || isOnEdge || !focusable.some((item) => item === active)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
      }
    };

    document.addEventListener("keydown", keepTabInside);
    return () => {
      document.removeEventListener("keydown", keepTabInside);
      returnTo?.focus();
    };
  }, [show, manageFocus]);

  return show ? (
    <Overlay onClick={isCloseable ? onClose : undefined}>
      <Window
        ignoreHeightAndScroll={ignoreHeightAndScroll}
        width={width}
        height={height}
        minHeight={minHeight}
        maxHeight={maxHeight}
        onClick={(e) => e.stopPropagation()}
        ref={windowRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={manageFocus ? -1 : undefined}
      >
        <Card padding={padding}>
          {(showCloseButton ?? isCloseable) && (
            <CloseButton onClick={onClose} aria-label="Close">
              <CloseSmall size={24} fill={theme.colors.ui.icon.brand} />
            </CloseButton>
          )}
          {children}
        </Card>
      </Window>
    </Overlay>
  ) : null;
};

export default ModalWindow;
